import type { View } from '../router/router';
import { apiClient } from '../services/api-client';
import { toast } from '../components/layout/toast';
import { getTyrisIcon } from '../components/ui/tyris-icon';

interface Exercise {
  name: string;
  description?: string;
  machine?: { name: string; code: string };
  gifPath?: string;
  restSec?: number;
}

interface RoutineExercise {
  sets: number;
  reps: string;
  weight: number;
  rpe?: number;
  rir?: number;
  restSec?: number | null;
  order: number;
  exercise: Exercise;
}

interface Routine {
  id: string;
  name: string;
  description: string;
  exercises: RoutineExercise[];
}

export class DailyRoutinePage implements View {
  private routines: Routine[] = [];
  private activeIndex = 0;
  private loading = true;
  private errorMsg: string | null = null;

  async render(): Promise<string> {
    await this.fetchActiveRoutines();

    if (this.loading) {
      return `<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:60vh;gap:1rem;">
        <div style="color:var(--tyris-energy);animation:spin 1s linear infinite;">
          ${getTyrisIcon('en-curso', { size: 36 })}
        </div>
        <p style="color:var(--text-muted);">Cargando tus rutinas activas...</p>
      </div>`;
    }

    if (this.errorMsg) {
      return `<div class="card-glass card-workout" style="text-align:center;padding:3rem 1.5rem;margin-top:2rem;">
        <div style="color:var(--color-error);margin-bottom:1rem;">
          ${getTyrisIcon('error', { size: 48 })}
        </div>
        <h3 style="font-size:1.5rem;margin-bottom:0.5rem;">Error de Conexión</h3>
        <p style="color:var(--text-muted);margin-bottom:1.5rem;">${this.errorMsg}</p>
        <button onclick="window.location.reload()" class="btn btn-student" style="width:auto;padding:0.75rem 2rem;">Reintentar</button>
      </div>`;
    }

    if (this.routines.length === 0) {
      return `
        <div class="dashboard-header" style="margin-bottom:1.5rem;">
          <div class="dashboard-title">
            <h2 style="font-size:1.6rem; font-weight:800;">Rutina del Día</h2>
            <p style="color:var(--text-muted);">Tu planificación diaria para hoy.</p>
          </div>
        </div>
        <div class="card-glass card-workout" style="text-align:center;padding:4rem 2rem;margin-top:2rem;display:flex;flex-direction:column;align-items:center;justify-content:center;">
          <div style="margin-bottom:1.5rem;color:var(--tyris-energy);">
            ${getTyrisIcon('rutina-del-dia', { size: 64 })}
          </div>
          <h3 style="font-size:1.8rem;margin-bottom:0.75rem;color:var(--tyris-energy);font-weight:700;">¡Día de Descanso!</h3>
          <p style="color:var(--text-muted);max-width:460px;line-height:1.6;margin-bottom:2rem;">
            No tienes rutinas activas asignadas. Aprovecha para descansar, estirar o hacer cardio ligero.
          </p>
          <a href="#/routines" class="btn btn-outline" style="width:auto;padding:0.75rem 2.5rem;">Ver Historial de Rutinas</a>
        </div>`;
    }

    return this.buildHtml();
  }

  private buildHtml(): string {
    const routine = this.routines[this.activeIndex];
    const sortedExercises = [...(routine?.exercises ?? [])].sort((a, b) => a.order - b.order);

    const tabsHtml = this.routines.length > 1
      ? `<div id="routine-tabs" style="display:flex;gap:0.5rem;flex-wrap:wrap;margin-bottom:1.5rem;">
          ${this.routines.map((r, i) => `
            <button class="btn ${i === this.activeIndex ? 'btn-student' : 'btn-outline'}"
              data-tab="${i}"
              style="width:auto;padding:0.5rem 1.1rem;font-size:0.88rem;">
              ${i + 1}. ${r.name}
            </button>`).join('')}
         </div>`
      : '';

    return `
      <div class="dashboard-header" style="margin-bottom:1.5rem;">
        <div class="dashboard-title">
          <h2 style="font-size:1.6rem; font-weight:800;">Rutina del Día</h2>
          <p style="color:var(--text-muted);">Supera tus límites y mantén la disciplina. ¡Cada repetición cuenta!</p>
        </div>
      </div>

      ${tabsHtml}

      <div class="card-glass card-workout" style="margin-bottom:2rem;">
        <div style="font-size:0.8rem;text-transform:uppercase;letter-spacing:0.05em;color:var(--tyris-energy);font-weight:800;margin-bottom:0.25rem;">
          Programa Activo ${this.routines.length > 1 ? `(${this.activeIndex + 1}/${this.routines.length})` : ''}
        </div>
        <h3 style="font-size:1.6rem;margin-bottom:0.5rem;font-weight:700;">${routine?.name ?? ''}</h3>
        <p style="color:var(--text-muted);font-size:1rem;line-height:1.5;">${routine?.description ?? ''}</p>
      </div>

      <h3 style="font-size:1.35rem;margin-bottom:1rem;font-weight:600;display:flex;align-items:center;gap:0.5rem;">
        Ejercicios Planificados <span style="font-size:0.95rem;color:var(--text-muted);font-weight:400;">(${sortedExercises.length})</span>
      </h3>

      <div style="display:flex;flex-direction:column;gap:1.25rem;">
        ${sortedExercises.map((item, idx) => {
          const restSec = item.restSec ?? item.exercise.restSec ?? 120;
          const machineBadge = item.exercise.machine
            ? `<span class="badge" style="background-color:rgba(255,255,255,0.05);color:var(--text-muted);border:1px solid var(--border-metallic);font-size:0.7rem;padding:0.15rem 0.5rem;">
                Máquina: ${item.exercise.machine.name} (${item.exercise.machine.code})</span>`
            : '';
          const rpeGlow = item.rpe && item.rpe >= 8 ? 'color:var(--tyris-honor);' : 'color:var(--text-main);';
          
          const gifButton = item.exercise.gifPath
            ? `<div style="display:flex;justify-content:center;">
                <button class="btn btn-outline btn-sm btn-gif-preview" data-gif="${item.exercise.gifPath}" data-name="${item.exercise.name}" style="padding:0.45rem 0.9rem;font-size:0.82rem;display:inline-flex;align-items:center;gap:0.4rem;border-color:rgba(111,190,149,0.4);color:var(--tyris-energy);white-space:nowrap;">
                  ${getTyrisIcon('ver-técnica', { size: 16 })}
                  <span>Ver Técnica</span>
                </button>
              </div>`
            : '';

          return `
            <div class="card-glass card-workout" style="padding:1.25rem;">
              <div style="display:flex;flex-direction:column;gap:0.85rem;">
                <div style="display:flex;align-items:center;gap:0.75rem;flex-wrap:wrap;">
                  <span style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;flex-shrink:0;border-radius:var(--radius-full);background:rgba(111,190,149,0.18);color:var(--tyris-energy);font-weight:700;font-size:0.85rem;">${idx + 1}</span>
                  <h4 style="font-size:1.1rem;font-weight:700;margin:0;">${item.exercise.name}</h4>
                  ${machineBadge}
                </div>
                ${item.exercise.description ? `<p style="color:var(--text-muted);font-size:0.85rem;margin:0;line-height:1.4;">${item.exercise.description}</p>` : ''}
                <div style="display:flex;flex-wrap:wrap;gap:0.85rem 1.25rem;background:rgba(0,0,0,0.2);padding:0.75rem 1rem;border-radius:var(--radius-md);border:1px solid var(--border-metallic);">
                  <div style="flex:1 1 auto;min-width:46px;text-align:center;"><div style="font-size:1.15rem;font-weight:700;color:var(--tyris-energy);">${item.sets}</div><div style="font-size:0.65rem;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-muted);font-weight:600;">Sets</div></div>
                  <div style="flex:1 1 auto;min-width:46px;text-align:center;"><div style="font-size:1.15rem;font-weight:700;color:var(--text-main);">${item.reps}</div><div style="font-size:0.65rem;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-muted);font-weight:600;">Reps</div></div>
                  <div style="flex:1 1 auto;min-width:58px;text-align:center;"><div style="font-size:1.15rem;font-weight:700;color:var(--tyris-energy);">${item.weight} kg</div><div style="font-size:0.65rem;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-muted);font-weight:600;">Peso</div></div>
                  ${item.rpe ? `<div style="flex:1 1 auto;min-width:46px;text-align:center;"><div style="font-size:1.15rem;font-weight:700;${rpeGlow}">@${item.rpe}</div><div style="font-size:0.65rem;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-muted);font-weight:600;">RPE</div></div>` : ''}
                  ${item.rir != null ? `<div style="flex:1 1 auto;min-width:46px;text-align:center;"><div style="font-size:1.15rem;font-weight:700;color:var(--text-muted);">${item.rir}RIR</div><div style="font-size:0.65rem;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-muted);font-weight:600;">RIR</div></div>` : ''}
                  <div style="flex:1 1 auto;min-width:58px;text-align:center;"><div style="font-size:1.15rem;font-weight:700;color:var(--text-main);">${restSec} s</div><div style="font-size:0.65rem;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-muted);font-weight:600;">Descanso</div></div>
                </div>
                ${gifButton}
              </div>
            </div>`;
        }).join('')}
      </div>

      <div id="exercise-gif-modal" class="modal-overlay" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:1000;align-items:center;justify-content:center;backdrop-filter:blur(5px);padding:1rem;">
        <div class="modal-box card-glass" style="max-width:480px;width:100%;padding:1.25rem;display:flex;flex-direction:column;gap:0.85rem;position:relative;">
          <button id="close-gif-modal" style="position:absolute;top:0.85rem;right:0.85rem;background:none;border:none;color:var(--text-muted);font-size:1.4rem;cursor:pointer;line-height:1;">✕</button>
          <h3 id="gif-modal-title" style="margin:0;font-size:1.1rem;font-weight:700;padding-right:2rem;color:var(--tyris-energy);"></h3>
          <div style="display:flex;justify-content:center;align-items:center;background:rgba(0,0,0,0.4);border-radius:var(--radius-md);overflow:hidden;border:1px solid var(--border-metallic);">
            <img id="gif-modal-img" src="" alt="Demostración de ejercicio" style="max-width:100%;max-height:55vh;object-fit:contain;display:block;" />
          </div>
        </div>
      </div>
    `;
  }

  afterRender?(): void {
    document.getElementById('routine-tabs')?.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest('[data-tab]') as HTMLButtonElement | null;
      if (!btn) return;
      this.activeIndex = parseInt(btn.dataset.tab!);
      const container = document.querySelector('#app-view-area') ?? document.body;
      container.innerHTML = this.buildHtml();
      this.afterRender?.();
    });

    document.querySelectorAll('.btn-gif-preview').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLButtonElement;
        const gif = target.getAttribute('data-gif');
        const name = target.getAttribute('data-name');
        
        const modal = document.getElementById('exercise-gif-modal');
        const title = document.getElementById('gif-modal-title');
        const img = document.getElementById('gif-modal-img') as HTMLImageElement;
        
        if (modal && title && img && gif) {
          title.textContent = name ?? '';
          img.src = gif;
          modal.style.display = 'flex';
        }
      });
    });

    const closeModal = () => {
      const modal = document.getElementById('exercise-gif-modal');
      const img = document.getElementById('gif-modal-img') as HTMLImageElement;
      if (modal) modal.style.display = 'none';
      if (img) img.src = '';
    };

    document.getElementById('close-gif-modal')?.addEventListener('click', closeModal);
    document.getElementById('exercise-gif-modal')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) closeModal();
    });
  }

  private async fetchActiveRoutines(): Promise<void> {
    try {
      const list = await apiClient.get<Routine[]>('/api/routines/my-active-list');
      this.routines = list ?? [];
      this.errorMsg = null;
    } catch (err: any) {
      if (err.status === 404) {
        this.routines = [];
        this.errorMsg = null;
      } else {
        console.error('Error al cargar las rutinas activas:', err);
        this.errorMsg = err.message || 'No se pudo establecer conexión con el servidor.';
        toast.error('Error al sincronizar tus rutinas activas.');
      }
    } finally {
      this.loading = false;
    }
  }
}
